import * as React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchableDropdown, {
  SearchableDropdownOption,
} from 'components/Dropdown/SearchableDropdown';

export type SlaRow = {
  id: string;
  startTime: string;
  endTime: string;
  critical: boolean;
  enabled: boolean;
};

export type SlaFormValues = {
  job: SearchableDropdownOption | null;
  slaType: SearchableDropdownOption | null;
  condition: SearchableDropdownOption | null;
  count: SearchableDropdownOption | null;
  criteria: SearchableDropdownOption | null;
  rows: SlaRow[];
};

type SlaFieldLabels = {
  modalTitle?: string;
  jobLabel?: string;
  slaTypeLabel?: string;
  conditionLabel?: string;
  countLabel?: string;
  criteriaLabel?: string;
  rowSectionTitle?: string;
  criticalityTitle?: string;
  startTimeLabel?: string;
  endTimeLabel?: string;
  addMoreLabel?: string;
  saveButtonLabel?: string;
  cancelButtonLabel?: string;
};

type ConfigurableSlaModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (values: SlaFormValues) => void;

  title?: string;

  jobOptions: SearchableDropdownOption[];
  slaTypeOptions: SearchableDropdownOption[];
  conditionOptions: SearchableDropdownOption[];
  countOptions: SearchableDropdownOption[];
  criteriaOptions: SearchableDropdownOption[];

  labels?: SlaFieldLabels;

  disableJob?: boolean;
  disableSlaType?: boolean;
  disableCondition?: boolean;
  disableCount?: boolean;
  disableCriteria?: boolean;
  disableSave?: boolean;

  minRows?: number;
  maxRows?: number;
};

type RowErrors = {
  startTime?: string;
  endTime?: string;
  overlap?: string;
};

type FormErrors = {
  job?: string;
  slaType?: string;
  condition?: string;
  count?: string;
  criteria?: string;
  rows?: string;
  rowErrors: Record<string, RowErrors>;
};

const createRow = (): SlaRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  startTime: '',
  endTime: '',
  critical: false,
  enabled: true,
});

const defaultLabels: Required<SlaFieldLabels> = {
  modalTitle: 'Configure SLA',
  jobLabel: 'Select Job',
  slaTypeLabel: 'SLA Type',
  conditionLabel: 'Run Time Condition',
  countLabel: 'Count',
  criteriaLabel: 'SLA Criteria',
  rowSectionTitle: 'Configure SLA Start - End Time',
  criticalityTitle: 'SLA Criticality',
  startTimeLabel: 'SLA Start Time (CST)',
  endTimeLabel: 'SLA End Time (CST)',
  addMoreLabel: 'Add More',
  saveButtonLabel: 'Save',
  cancelButtonLabel: 'Cancel',
};

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const hasValidTime = (time: string) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);

const ConfigurableSlaFormModal: React.FC<ConfigurableSlaModalProps> = ({
  open,
  onClose,
  onSave,
  title,
  jobOptions,
  slaTypeOptions,
  conditionOptions,
  countOptions,
  criteriaOptions,
  labels,
  disableJob = false,
  disableSlaType = false,
  disableCondition = false,
  disableCount = false,
  disableCriteria = false,
  disableSave = false,
  minRows = 1,
  maxRows,
}) => {
  const theme = useTheme();
  const colors = theme.palette;
  const mergedLabels = { ...defaultLabels, ...labels };

  const getInitialState = React.useCallback((): SlaFormValues => {
    return {
      job: null,
      slaType: null,
      condition: null,
      count: null,
      criteria: null,
      rows: [createRow()],
    };
  }, []);

  const [formValues, setFormValues] = React.useState<SlaFormValues>(getInitialState);
  const [errors, setErrors] = React.useState<FormErrors>({
    rowErrors: {},
  });

  React.useEffect(() => {
    if (open) {
      setFormValues(getInitialState());
      setErrors({ rowErrors: {} });
    }
  }, [open, getInitialState]);

  const handleDropdownChange = (
    key: keyof Omit<SlaFormValues, 'rows'>,
    value: SearchableDropdownOption | null,
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleRowChange = (
    rowId: string,
    key: keyof Omit<SlaRow, 'id'>,
    value: string | boolean,
  ) => {
    setFormValues((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => (row.id === rowId ? { ...row, [key]: value } : row)),
    }));
  };

  const handleAddRow = () => {
    if (maxRows && formValues.rows.length >= maxRows) return;

    setFormValues((prev) => ({
      ...prev,
      rows: [...prev.rows, createRow()],
    }));
  };

  const handleDeleteRow = (rowId: string) => {
    if (formValues.rows.length <= minRows) return;

    setFormValues((prev) => ({
      ...prev,
      rows: prev.rows.filter((row) => row.id !== rowId),
    }));
  };

  const validateForm = (): boolean => {
    const nextErrors: FormErrors = {
      job: !formValues.job ? 'Job is required' : undefined,
      slaType: !formValues.slaType ? 'SLA Type is required' : undefined,
      condition: !formValues.condition ? 'Condition is required' : undefined,
      count: !formValues.count ? 'Count is required' : undefined,
      criteria: !formValues.criteria ? 'SLA Criteria is required' : undefined,
      rows: undefined,
      rowErrors: {},
    };

    if (!formValues.rows.length) {
      nextErrors.rows = 'At least one SLA row is required';
    }

    formValues.rows.forEach((row) => {
      const rowError: RowErrors = {};

      if (!row.startTime) {
        rowError.startTime = 'Start time is required';
      } else if (!hasValidTime(row.startTime)) {
        rowError.startTime = 'Enter a valid time';
      }

      if (!row.endTime) {
        rowError.endTime = 'End time is required';
      } else if (!hasValidTime(row.endTime)) {
        rowError.endTime = 'Enter a valid time';
      }

      if (
        row.startTime &&
        row.endTime &&
        hasValidTime(row.startTime) &&
        hasValidTime(row.endTime)
      ) {
        if (timeToMinutes(row.endTime) <= timeToMinutes(row.startTime)) {
          rowError.endTime = 'End time must be greater than start time';
        }
      }

      if (Object.keys(rowError).length > 0) {
        nextErrors.rowErrors[row.id] = rowError;
      }
    });

    const validRows = formValues.rows.filter(
      (row) =>
        hasValidTime(row.startTime) &&
        hasValidTime(row.endTime) &&
        timeToMinutes(row.endTime) > timeToMinutes(row.startTime),
    );

    for (let i = 0; i < validRows.length; i += 1) {
      for (let j = i + 1; j < validRows.length; j += 1) {
        const current = validRows[i];
        const compare = validRows[j];

        const currentStart = timeToMinutes(current.startTime);
        const currentEnd = timeToMinutes(current.endTime);
        const compareStart = timeToMinutes(compare.startTime);
        const compareEnd = timeToMinutes(compare.endTime);

        const isOverlap = currentStart < compareEnd && compareStart < currentEnd;

        if (isOverlap) {
          nextErrors.rowErrors[current.id] = {
            ...nextErrors.rowErrors[current.id],
            overlap: 'Time range overlaps with another SLA row',
          };

          nextErrors.rowErrors[compare.id] = {
            ...nextErrors.rowErrors[compare.id],
            overlap: 'Time range overlaps with another SLA row',
          };
        }
      }
    }

    setErrors(nextErrors);

    const hasTopLevelError = Boolean(
      nextErrors.job ||
        nextErrors.slaType ||
        nextErrors.condition ||
        nextErrors.count ||
        nextErrors.criteria ||
        nextErrors.rows,
    );

    const hasRowErrors = Object.keys(nextErrors.rowErrors).length > 0;

    return !hasTopLevelError && !hasRowErrors;
  };

  const handleSave = () => {
    const isValid = validateForm();
    if (!isValid) return;
    onSave(formValues);
  };

  const canDelete = formValues.rows.length > minRows;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '1180px',
          maxWidth: 'calc(100vw - 64px)',
          minWidth: '1180px',
          borderRadius: '4px',
          overflow: 'hidden',
          backgroundColor: `${colors.modal.bg} !important`,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 64px)',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: colors.modal.titleBg,
          color: colors.modal.title,
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="widgetTitle">{title || mergedLabels.modalTitle}</Typography>

        <IconButton onClick={onClose} aria-label="close">
          <CloseIcon sx={{ color: colors.modal.title }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 2,
          mt: 2,
          pb: 0,
          flex: 1,
          overflowY: 'auto',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SearchableDropdown
              label={mergedLabels.jobLabel}
              value={formValues.job}
              setValue={(value: SearchableDropdownOption | null) =>
                handleDropdownChange('job', value)
              }
              options={jobOptions}
              placeholder={mergedLabels.jobLabel}
              required
              disabled={disableJob}
              error={Boolean(errors.job)}
              helperText={errors.job}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SearchableDropdown
              label={mergedLabels.slaTypeLabel}
              value={formValues.slaType}
              setValue={(value: SearchableDropdownOption | null) =>
                handleDropdownChange('slaType', value)
              }
              options={slaTypeOptions}
              placeholder={mergedLabels.slaTypeLabel}
              required
              disabled={disableSlaType}
              error={Boolean(errors.slaType)}
              helperText={errors.slaType}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <SearchableDropdown
              label={mergedLabels.conditionLabel}
              value={formValues.condition}
              setValue={(value: SearchableDropdownOption | null) =>
                handleDropdownChange('condition', value)
              }
              options={conditionOptions}
              placeholder={mergedLabels.conditionLabel}
              required
              disabled={disableCondition}
              error={Boolean(errors.condition)}
              helperText={errors.condition}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <SearchableDropdown
              label={mergedLabels.countLabel}
              value={formValues.count}
              setValue={(value: SearchableDropdownOption | null) =>
                handleDropdownChange('count', value)
              }
              options={countOptions}
              placeholder={mergedLabels.countLabel}
              required
              disabled={disableCount}
              error={Boolean(errors.count)}
              helperText={errors.count}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SearchableDropdown
              label={mergedLabels.criteriaLabel}
              value={formValues.criteria}
              setValue={(value: SearchableDropdownOption | null) =>
                handleDropdownChange('criteria', value)
              }
              options={criteriaOptions}
              placeholder={mergedLabels.criteriaLabel}
              required
              disabled={disableCriteria}
              error={Boolean(errors.criteria)}
              helperText={errors.criteria}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 180px' },
              gap: 2,
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  color: colors.modal.sectionTitle,
                  fontWeight: 600,
                  fontSize: '18px',
                  whiteSpace: 'nowrap',
                }}
              >
                {mergedLabels.rowSectionTitle}
              </Typography>
              <Divider sx={{ flex: 1, borderColor: colors.modal.sectionTitle }} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  color: colors.modal.sectionTitle,
                  fontWeight: 600,
                  fontSize: '18px',
                  whiteSpace: 'nowrap',
                }}
              >
                {mergedLabels.criticalityTitle}
              </Typography>
              <Divider sx={{ flex: 1, borderColor: colors.modal.sectionTitle }} />
            </Box>
          </Box>

          {errors.rows ? (
            <Typography sx={{ color: colors.error.main, mb: 2 }}>{errors.rows}</Typography>
          ) : null}

          {formValues.rows.map((row, index) => {
            const rowError = errors.rowErrors[row.id] || {};

            return (
              <Box key={row.id} sx={{ mb: 2 }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      type="time"
                      label={mergedLabels.startTimeLabel}
                      value={row.startTime}
                      onChange={(e) => handleRowChange(row.id, 'startTime', e.target.value)}
                      error={Boolean(rowError.startTime)}
                      helperText={rowError.startTime}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: row.startTime
                              ? colors.inputNew.inputBorderFocus
                              : colors.inputNew.inputBorder,
                          },
                          '&:hover fieldset': {
                            borderColor: colors.inputNew.inputBorderHover,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.inputNew.inputBorderFocus,
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: colors.inputNew.inputText,
                        },
                        '& .MuiInputLabel-root': {
                          color: `${colors.inputNew.labelText} !important`,
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: `${colors.inputNew.labelText} !important`,
                        },
                        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                          color: `${colors.inputNew.labelText} !important`,
                        },
                        '& .MuiFormLabel-root': {
                          color: `${colors.inputNew.labelText} !important`,
                        },
                        '& .MuiFormLabel-root.Mui-focused': {
                          color: `${colors.inputNew.labelText} !important`,
                        },
                        '& .MuiSvgIcon-root': {
                          color: colors.inputNew.inputIcon,
                        },
                        '& input[type="time"]::-webkit-calendar-picker-indicator': {
                          opacity: 1,
                          cursor: 'pointer',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      type="time"
                      label={mergedLabels.endTimeLabel}
                      value={row.endTime}
                      onChange={(e) => handleRowChange(row.id, 'endTime', e.target.value)}
                      error={Boolean(rowError.endTime)}
                      helperText={rowError.endTime}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: row.endTime
                              ? colors.inputNew.inputBorderFocus
                              : colors.inputNew.inputBorder,
                          },
                          '&:hover fieldset': {
                            borderColor: colors.inputNew.inputBorderHover,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.inputNew.inputBorderFocus,
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: colors.inputNew.inputText,
                        },
                        '& .MuiInputLabel-root': {
                          color: `${colors.inputNew.labelText} !important`,
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: `${colors.inputNew.labelText} !important`,
                        },
                        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                          color: `${colors.inputNew.labelText} !important`,
                        },
                        '& .MuiFormLabel-root': {
                          color: `${colors.inputNew.labelText} !important`,
                        },
                        '& .MuiFormLabel-root.Mui-focused': {
                          color: `${colors.inputNew.labelText} !important`,
                        },
                        '& .MuiSvgIcon-root': {
                          color: colors.inputNew.inputIcon,
                        },
                        '& input[type="time"]::-webkit-calendar-picker-indicator': {
                          opacity: 1,
                          cursor: 'pointer',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={8} md={1}>
                    <Box
                      sx={{
                        minHeight: '56px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: { xs: 'flex-start', md: 'center' },
                      }}
                    >
                      <Switch
                        checked={row.critical}
                        onChange={(e) => handleRowChange(row.id, 'critical', e.target.checked)}
                        sx={{
                          width: 42,
                          height: 26,
                          padding: 0,
                          display: 'flex',
                          '& .MuiSwitch-switchBase': {
                            padding: '3px',
                            transitionDuration: '200ms',
                            '&.Mui-checked': {
                              transform: 'translateX(16px)',
                              color: colors.toggle.thumb,
                              '& + .MuiSwitch-track': {
                                backgroundColor: colors.toggle.trackOn,
                                opacity: 1,
                                border: 'none',
                              },
                            },
                            '&.Mui-disabled + .MuiSwitch-track': {
                              opacity: 0.5,
                            },
                          },
                          '& .MuiSwitch-thumb': {
                            boxSizing: 'border-box',
                            width: 20,
                            height: 20,
                            backgroundColor: colors.toggle.thumb,
                            boxShadow: colors.toggle.thumbShadow,
                          },
                          '& .MuiSwitch-track': {
                            borderRadius: 13,
                            backgroundColor: colors.toggle.trackOff,
                            opacity: 1,
                            transition: 'background-color 200ms',
                          },
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={4} md={1}>
                    <Box
                      sx={{
                        minHeight: '56px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: { xs: 'flex-end', md: 'center' },
                      }}
                    >
                      {canDelete ? (
                        <IconButton
                          onClick={() => handleDeleteRow(row.id)}
                          aria-label={`delete-sla-row-${index}`}
                          sx={{
                            border: `1px solid ${colors.inputNew.removeBorder}`,
                            borderRadius: '4px',
                            width: 40,
                            height: 40,
                            '&:hover': {
                              backgroundColor: colors.inputNew.removeHoverBackground,
                            },
                          }}
                        >
                          <CloseIcon sx={{ color: colors.inputNew.removeIcon }} />
                        </IconButton>
                      ) : null}
                    </Box>
                  </Grid>

                  {rowError.overlap ? (
                    <Grid item xs={12}>
                      <Typography sx={{ color: colors.error.main, mt: -1 }}>
                        {rowError.overlap}
                      </Typography>
                    </Grid>
                  ) : null}
                </Grid>
              </Box>
            );
          })}

          {(!maxRows || formValues.rows.length < maxRows) && (
            <Button
              variant="text"
              startIcon={<AddRoundedIcon sx={{ color: colors.inputNew.addText }} />}
              onClick={handleAddRow}
              sx={{
                mt: 1,
                px: 0,
                color: colors.inputNew.addText,
                textTransform: 'none',
                fontWeight: 500,
                '& .MuiButton-startIcon': {
                  color: colors.inputNew.addText,
                },
              }}
            >
              {mergedLabels.addMoreLabel}
            </Button>
          )}
        </Box>

        <Box sx={{ height: 16 }} />
      </DialogContent>

      <Box
        sx={{
          px: 2,
          py: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          flexWrap: 'wrap',
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          flexShrink: 0,
        }}
      >
        <Button
          variant="pepwiseButtonText"
          onClick={onClose}
          sx={{
            minWidth: 120,
            textTransform: 'uppercase',
            color: colors.buttons.primary.color,
          }}
        >
          {mergedLabels.cancelButtonLabel}
        </Button>

        <Button
          variant="pepwiseSecondary"
          onClick={handleSave}
          disabled={disableSave}
          sx={{
            minWidth: 160,
            backgroundColor: colors.buttons.primary.color,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: colors.buttons.primary.bgColor,
              boxShadow: 'none',
            },
            textTransform: 'uppercase',
          }}
        >
          {mergedLabels.saveButtonLabel}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ConfigurableSlaFormModal;
